export const TRIVIA_EDITOR_DRAFT_PREFIX = 'trivia-editor-draft:';

export type TriviaEditorDraft = {
  engagementId: string;
  title: string;
  description: string;
  qrCodeCta: string;
  config: Record<string, unknown> & { questions?: unknown };
  saveError: string | null;
  updatedAt: string;
};

export function triviaEditorDraftKey(engagementId: string) {
  return `${TRIVIA_EDITOR_DRAFT_PREFIX}${engagementId}`;
}

export function loadTriviaEditorDraft(
  engagementId: string,
  storage: Pick<Storage, 'getItem'> = globalThis.localStorage,
): TriviaEditorDraft | null {
  if (!engagementId || !storage) return null;
  try {
    const raw = storage.getItem(triviaEditorDraftKey(engagementId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TriviaEditorDraft;
    if (!parsed || parsed.engagementId !== engagementId || !parsed.config) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveTriviaEditorDraft(
  draft: TriviaEditorDraft,
  storage: Pick<Storage, 'setItem'> = globalThis.localStorage,
) {
  if (!draft.engagementId || !storage) return;
  storage.setItem(
    triviaEditorDraftKey(draft.engagementId),
    JSON.stringify(draft),
  );
}

export function clearTriviaEditorDraft(
  engagementId: string,
  storage: Pick<Storage, 'removeItem'> = globalThis.localStorage,
) {
  if (!engagementId || !storage) return;
  storage.removeItem(triviaEditorDraftKey(engagementId));
}

export function draftFromEditorState(args: {
  engagementId: string;
  title?: string | null;
  description?: string | null;
  qrCodeCta?: string | null;
  config: TriviaEditorDraft['config'];
  saveError: string | null;
}): TriviaEditorDraft {
  return {
    engagementId: args.engagementId,
    title: args.title ?? '',
    description: args.description ?? '',
    qrCodeCta: args.qrCodeCta ?? '',
    config: args.config,
    saveError: args.saveError,
    updatedAt: new Date().toISOString(),
  };
}

export function applyDraftToEditor<T extends Record<string, unknown>>(
  editor: T,
  draft: TriviaEditorDraft,
): T {
  return {
    ...editor,
    title: draft.title,
    description: draft.description,
    qrCodeCta: draft.qrCodeCta,
    config: draft.config,
  };
}
